import { FeatureModule } from '../core/tenant/feature.model';
import { FlujoDeVenta } from '../services/table.service';

/**
 * LA AYUDA DEL POS, COMO DATOS (plan docs/planes/ONBOARDING-Y-AYUDA.md; textos de Producto en
 * docs/planes/TEXTOS-DE-PANTALLA.md §M16). Solo texto: EDDGM y Producto lo leen tal cual. El POS no tiene guarda de
 * concordancia, así que no lleva {termino}.
 *
 * Una entrada sale si el negocio tiene su `modulo` (FeatureService) y, en /pos, si su `flujo` es el de la sede.
 */
export interface AyudaDePantalla {
    ruta: string;
    /** /pos cambia entera según el flujo de venta de la sede. */
    flujo?: FlujoDeVenta;
    /** El módulo que abre la ruta (su featureGuard). Sin él, no depende de ninguno. */
    modulo?: FeatureModule;
    titulo: string;
    paraQue: string;
    comoSeUsa: string[];
    enLaBienvenida: boolean;
    /** A7 (misiones 3.3.0, Producto): de 2 a 4 pasos que señalan dónde dar clic (`data-ayuda` del elemento). Sin ancla, se salta. */
    pasos?: { ancla: string; texto: string }[];
}

export const BIENVENIDA = {
    titulo: 'Te damos la bienvenida a tu caja',
    // Producto (docs 6983ef3): igual a la del panel; la bienvenida no vuelve con el (?).
    intro: 'En unos pocos pasos te mostramos lo principal. La ayuda de cada pantalla está en el botón (?).',
};

/** Los mismos del panel (Producto, §M16). */
export const BOTONES = {
    omitir: 'Omitir',
    anterior: 'Anterior',
    siguiente: 'Siguiente',
    listo: 'Listo',
    cerrar: 'Cerrar',
    ayuda: 'Ayuda de esta pantalla',
    comoSeUsa: 'Cómo se usa',
    sinAyudaTitulo: 'Esta pantalla todavía no tiene ayuda',
};

export const AYUDA_DE_PANTALLAS: AyudaDePantalla[] = [
    {
        ruta: '/pos',
        flujo: 'DIRECTO',
        modulo: 'ventas',
        titulo: 'Crear Órdenes',
        paraQue: 'Aquí vendes: escaneas o escribes, y cobras.',
        comoSeUsa: [
            'Escanea o escribe el nombre, y ⏎ añade. Con productos en la orden, ⏎ cobra en efectivo.',
            'Para varias unidades, escribe la cantidad y * antes (3*).',
            'F8 abre el efectivo para escribir lo recibido, F9 cobra con otro medio y Esc vacía la orden.',
            'Marca hasta 12 favoritos para venderlos con F1 a F12.',
        ],
        enLaBienvenida: true,
        // A7, piloto (Producto, MISIONES-3.3.0-RESPUESTAS-PRODUCTO §A7). Sin favoritos marcados, su paso se salta.
        pasos: [
            { ancla: 'buscador', texto: 'Escanea o escribe el nombre aquí, y ⏎ lo añade.' },
            { ancla: 'orden', texto: 'Aquí se arma la venta. Con productos en la orden, ⏎ cobra en efectivo.' },
            { ancla: 'cobrar', texto: 'Cobra aquí: F8 abre el efectivo y F9 cobra con otro medio.' },
            { ancla: 'favoritos', texto: 'Marca hasta 12 favoritos para venderlos con F1 a F12.' },
        ],
    },
    {
        ruta: '/pos',
        flujo: 'MESA',
        modulo: 'ventas',
        titulo: 'Crear Órdenes',
        paraQue: 'Aquí tomas el pedido de cada mesa y lo cobras al final.',
        comoSeUsa: [
            'Elige la mesa.',
            'Añade lo que piden y usa «ENVIAR A COCINA».',
            'Cada ronda se suma a la cuenta de la misma mesa.',
            'Para cerrar la cuenta, «COBRAR MESA».',
        ],
        enLaBienvenida: true,
    },
    {
        ruta: '/pos',
        flujo: 'RASTREADOR',
        modulo: 'ventas',
        titulo: 'Crear Órdenes',
        paraQue: 'Aquí vendes y entregas el rastreador para avisar cuando el pedido esté listo.',
        comoSeUsa: [
            'Elige el grupo y el número del rastreador.',
            'Añade lo que piden.',
            'Cobra con «PAGAR ORDEN» y entrega el rastreador.',
        ],
        enLaBienvenida: true,
    },
    {
        ruta: '/history',
        modulo: 'historial',
        titulo: 'Historial',
        paraQue: 'Las ventas de la caja: buscarlas y volver a imprimirlas.',
        comoSeUsa: [
            'Busca por número de orden, o filtra por rastreador o mesa.',
            '«Ver» abre la venta y «Reimprimir» saca la tirilla otra vez (no abre el cajón).',
            'Borrar una venta pide motivo y la clave del negocio; solo lo hace el administrador.',
        ],
        enLaBienvenida: true,
    },
    {
        ruta: '/closure',
        modulo: 'cierre',
        titulo: 'Cierre de Caja',
        paraQue: 'Cierra tu turno: cuentas lo que hay y el sistema lo compara con lo vendido.',
        comoSeUsa: [
            'Cuenta billetes y monedas, y escribe lo de tarjeta y QR.',
            'Anota las salidas de efectivo del día.',
            'Usa «Cerrar el turno»: ahí escribes la base que dejas para el siguiente turno, que no puede ser mayor que lo contado.',
            'Lo esperado se ve después de cerrar.',
        ],
        enLaBienvenida: true,
    },
    {
        ruta: '/discounts',
        modulo: 'descuentos',
        titulo: 'Descuentos',
        paraQue: 'Los cupones de descuento que se aplican al cobrar.',
        comoSeUsa: [
            '«Nuevo Cupón», con código, porcentaje y fechas.',
            'Si no eliges productos, aplica a toda la orden.',
            'En la caja, se escribe en «Código de descuento» y se usa «APLICAR».',
        ],
        enLaBienvenida: false,
    },
    {
        ruta: '/ruta',
        titulo: 'Modo Ruta',
        paraQue: 'Lo que vendes y cobras en la calle, aunque no tengas señal.',
        comoSeUsa: [
            '«Hoy»: las visitas del día.',
            'Toma pedidos y cobros; se guardan en el teléfono.',
            'Al volver la señal, todo se envía solo; revisa que diga «Todo enviado».',
            '«Cierre», al terminar el día.',
        ],
        enLaBienvenida: false,
    },
    // Ayudas 26/10 (Producto, TEXTOS «Ayudas 26/10»): el Modo Bodega, una por tarjeta y una para la entrada; sin precios.
    {
        ruta: '/bodega',
        titulo: 'Modo Bodega',
        paraQue: 'Alistar, trasladar, contar, reubicar y recibir en la bodega, con el celular y el lector.',
        comoSeUsa: [
            'Toca lo que vas a hacer.',
            'Lo que haces queda en el celular y se envía solo. Arriba dice «Todo enviado» o cuántos registros faltan.',
            'Si el celular se comparte, cada persona entra con su PIN y «Cambiar de persona» deja entrar a otra. Lo de cada uno sale a su nombre.',
        ],
        enLaBienvenida: false,
    },
    {
        ruta: '/bodega/alistar',
        titulo: 'Alistar',
        paraQue: 'Los pedidos confirmados por despachar: escaneas cada producto y se va marcando lo alistado.',
        comoSeUsa: [
            'Toca el pedido y escanea cada producto. Para varios iguales, escribe la cantidad y * antes de escanear.',
            'Si no es el que pide, avisa «Ese no es». Si te pasaste, usa «Quitar uno».',
            'Al terminar, «Listo, alistado». Si faltan líneas, te pregunta: «Terminar con faltantes» o «Seguir alistando».',
        ],
        enLaBienvenida: false,
    },
    {
        ruta: '/bodega/trasladar',
        titulo: 'Trasladar',
        paraQue: 'Lo que sale de esta bodega hacia otro lugar del negocio. Necesita señal.',
        comoSeUsa: [
            'Elige de dónde sale y a dónde llega.',
            'Escanea o escribe el nombre, y pon la cantidad con − y +.',
            '«Despachar traslado»: queda en tránsito hasta que lo reciban en el destino.',
        ],
        enLaBienvenida: false,
    },
    {
        ruta: '/bodega/contar',
        titulo: 'Contar',
        paraQue: 'Escanea y escribe cuántos hay. Para encontrar el producto hace falta señal.',
        comoSeUsa: [
            'Elige dónde vas a contar y usa «Abrir conteo», o «Seguir el conteo» si ya hay uno abierto.',
            'Escanea, escribe la cantidad y toca «Siguiente». Si otra persona ya contó ahí, lo que escribas se suma.',
            '«Cerrar conteo» muestra las diferencias. El inventario no cambia hasta que un administrador lo asienta en el panel.',
        ],
        enLaBienvenida: false,
    },
    {
        ruta: '/bodega/reubicar',
        titulo: 'Reubicar',
        paraQue: 'Mueve mercancía de un puesto a otro dentro de la bodega. No cambia cuánto hay.',
        comoSeUsa: [
            'Escanea el producto y elige de dónde sale.',
            'Escribe cuántos, o toca «Todo».',
            'Escanea la etiqueta del puesto de destino o tócalo en la lista, y usa «Reubicar».',
        ],
        enLaBienvenida: false,
    },
    {
        ruta: '/bodega/recibir',
        titulo: 'Recibir',
        paraQue: 'Cuenta lo que llega contra la recepción que abrió la oficina.',
        comoSeUsa: [
            'Toca la recepción abierta. Si no hay ninguna, se abre desde el panel.',
            'Escanea cada producto y marca «Llegó», «Faltó», «De más» o «Averiado», con el lote y el vencimiento si los pide.',
            'Lo anotado queda guardado. Entra al inventario cuando un administrador lo recibe en el panel.',
        ],
        enLaBienvenida: false,
    },
    {
        ruta: '/consumo-propio',
        titulo: 'Consumo propio',
        paraQue: 'Lo que consumen el dueño o un empleado. No se cobra, no lleva factura y no entra en el cierre de caja.',
        comoSeUsa: [
            'En la caja, en «Tipo de venta», elige «Consumo propio» y quién lo consume. Usa «Registrar consumo».',
            'Aquí se ven por fechas, con su valor al precio de venta y quién lo registró.',
            'El administrador usa «Anular» con el motivo: los productos vuelven al inventario.',
            'Tu contador lo registra como retiro del dueño o como gasto de personal.',
        ],
        enLaBienvenida: false,
    },
];

/** Ayuda de campo del POS (M16, TEXTOS-DE-PANTALLA §M16 «Ayuda de campo»): el (?) junto a un campo difícil. */
export const AYUDA_DE_CAMPOS = {
    'pago.mixto': 'Reparte el total entre efectivo, tarjeta y QR. Usa al menos dos medios, y la suma tiene que ser exacta.',
    'cierre.base': 'Lo que dejas en la caja para el siguiente turno. Sale del efectivo contado, así que no puede ser mayor.',
    'cierre.salidas': 'Todo el efectivo que salió de la caja hoy: pagos a proveedores, compras pequeñas, adelantos.',
    'cierre.qr': 'Se llena solo con los pagos QR registrados en el panel. Si no aparecen, marca «Manual» y escríbelo.',
} as const;

export type ClaveDeCampo = keyof typeof AYUDA_DE_CAMPOS;

/** aria-label del (?): «Ayuda: {etiqueta del campo}», para que el lector de pantalla distinga los de una pantalla. */
export const etiquetaDeAyudaDeCampo = (campo: string): string => `Ayuda: ${campo}`;

/**
 * El mismo (?) en HTML para los diálogos de Swal, donde no entra un componente. `popover` nativo: lo abre el botón y lo
 * cierra Esc o un clic fuera; va en la capa superior, por encima del diálogo. Estilos en línea: el CSS del componente
 * no llega al HTML de Swal. Los textos son constantes de este fichero, sin datos del usuario.
 */
export function ayudaDeCampoHtml(clave: ClaveDeCampo, campo: string, id: string): string {
    const etiqueta = etiquetaDeAyudaDeCampo(campo);
    return `<button type="button" popovertarget="${id}" aria-label="${etiqueta}" title="${etiqueta}" data-testid="ayuda-campo"` +
        ` style="all:unset;box-sizing:border-box;display:inline-grid;place-items:center;width:18px;height:18px;margin-left:4px;` +
        `border-radius:50%;border:1px solid #cbd5e1;color:#475569;font-size:11px;font-weight:700;line-height:1;cursor:pointer;vertical-align:middle">?</button>` +
        `<span id="${id}" popover role="tooltip" style="max-width:min(320px,calc(100vw - 32px));margin:auto;padding:12px 14px;` +
        `border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#1e293b;font-size:.85rem;font-weight:normal;line-height:1.4;text-align:left">` +
        `${AYUDA_DE_CAMPOS[clave]}</span>`;
}
